import { useStore } from '@nanostores/react'
import { captureException } from '@/utilities/logger-fe'
import React, { type FC, useEffect, useMemo, useState } from 'react'

import { redeemPointsOnSubscription } from '@/clients/pops/client'
import type { MealProgramSubscription } from '@/models/meal-program-subscription'
import {
  $activeMealProgramSubscriptions,
  $isLoggedIn,
} from '@/stores/account/computed'
import { $customer } from '@/stores/account/stores'
import { getLoginHref } from '@/utilities/auth'

import { SelectInput } from '../Shared/SelectInput'

import './rewards-custom-points-redemption.scss'

interface RewardsCustomPointsRedemptionProps {
  redemptionOptionId: number
}

export const RewardsCustomPointsRedemption: FC<
  RewardsCustomPointsRedemptionProps
> = ({ redemptionOptionId }) => {
  const customer = useStore($customer)
  const subscriptions = useStore(
    $activeMealProgramSubscriptions,
  ) as MealProgramSubscription[]
  const isLoggedIn = useStore($isLoggedIn)
  const [selectedSubscription, setSelectedSubscription] =
    useState<MealProgramSubscription>()
  const [pointsBalance, setPointsBalance] = useState(0)
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const onRedeemPointsClick = async () => {
    setIsLoading(true)
    try {
      if (selectedSubscription) {
        await redeemPointsOnSubscription(
          selectedSubscription.id,
          redemptionOptionId,
        )

        setSuccessMessage('Your points have been redeemed!')
      }
      setIsLoading(false)
      setSelectedSubscription(undefined)
    } catch (error: any) {
      if (typeof error === 'string') {
        setIsLoading(false)
        if (error) {
          setErrorMessage(error)
          return
        }
      }
      setErrorMessage('Unable to redeem points for the selected subscription.')
    }
  }

  const fetchYotpoLoyaltyData = async () => {
    try {
      const response = await fetch('/api/yotpo-loyalty', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: customer?.email }),
      })
      const data = await response.json()

      setPointsBalance(data.points_balance)
    } catch (error) {
      captureException(error)
    }
  }

  const pointsErrorMessage = useMemo(() => {
    if (pointsBalance < 1250) {
      return `Looks like you don't have enough points for this product. This product costs 1250 points and you have ${pointsBalance} points.`
    }
    if (!subscriptions || !subscriptions.length) {
      return `Looks like you don't have any active subscriptions.`
    }
  }, [subscriptions, pointsBalance])

  const handleSubscriptionSelection = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const value = e.target.value
    if (value) {
      const selectedSub = subscriptions.find(
        (subscription) => subscription.externalSubscriptionId === value,
      )
      if (selectedSub) {
        setSelectedSubscription(selectedSub)
      }
    }
  }

  useEffect(() => {
    fetchYotpoLoyaltyData()
  }, [])

  return (
    <div>
      <div className="rewards-custom-points-redemption">
        <div className="rewards-custom-points-redemption__wrapper">
          <h2 className="rewards-custom-points-redemption__title">
            Redeem Points for Organic Meal Delivery
          </h2>
          <div
            tabIndex={0}
            className="rewards-custom-points-redemption__option"
          >
            <img
              src="/images/yotpo-reward-omd.webp"
              className="rewards-custom-points-redemption__option-image"
              alt="Meal program"
              loading="lazy"
            />
            <h3 className="rewards-custom-points-redemption__option-heading">
              $75 OFF ANY ORGANIC MEAL DELIVERY PROGRAM
            </h3>

            {isLoggedIn && (
              <div className="flex flex-col gap-8">
                {!errorMessage && !pointsErrorMessage && (
                  <div>
                    <p className="text-body redemption-subscription-selection-message">
                      Select a subscription to receive $75 off your next Organic
                      Meal Delivery program.
                    </p>
                    <SelectInput
                      onChange={(e) => handleSubscriptionSelection(e)}
                      placeholder="Select Subscription"
                      label=""
                      values={subscriptions.map(
                        (subscription) => subscription.externalSubscriptionId,
                      )}
                      required={false}
                      className="bg-white"
                    />
                  </div>
                )}

                {pointsErrorMessage && (
                  <p className="text-body">{pointsErrorMessage}</p>
                )}

                {errorMessage && (
                  <p className="text-body rewards-custom-points-redemption__error">
                    {errorMessage} Please contact&nbsp;
                    <a
                      href="mailto:help@sakaralife.com"
                      className="link link-reverse"
                    >
                      help@sakaralife.com
                    </a>
                    &nbsp;for assistance.
                  </p>
                )}

                {successMessage && (
                  <p className="text-body rewards-custom-points-redemption__success">
                    {successMessage}
                  </p>
                )}
                <hr
                  style={{
                    marginTop: '16px',
                    marginBottom: '10px',
                    color: '#efefef',
                  }}
                />
                <div className="rewards-custom-points-redemption__card-footer">
                  <p className="text-body">1250 POINTS</p>
                  <button
                    className={'button button--primary'}
                    onClick={() => onRedeemPointsClick()}
                    disabled={
                      !!errorMessage ||
                      !!pointsErrorMessage ||
                      !selectedSubscription ||
                      isLoading
                    }
                  >
                    Redeem now
                  </button>
                </div>
              </div>
            )}
          </div>
          {!isLoggedIn && (
            <div className="rewards-custom-points-redemption__logged-out-points-redeem">
              <p className="text-body">1250 POINTS</p>
              <a href={getLoginHref('/pages/rewards')} className="button">
                Redeem now
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
